import {
  compareExtraCoveragesOnPage,
  CoverageLevel,
  extraCoveragesPageAccessors,
  getExtraCoveragesMap,
  mapDollarsEnumToNumber,
  mapNumberEnumToNumber,
  mapPercentEnumToDisplayValue,
  ScreenWithCoverages,
  type CoverageOptions,
  type CoverageValueFormats,
  type ExtraCoverageAccessor,
} from "@covertree/new-business-mappings";
import type { EndorsementExtraCoveragesFragment } from "@covertree/agent-portal-graphql";
import type { OptionType } from "@covertree/types";
import { formatCustomizationOptions } from "@covertree/utils";

import type {
  ProjectedPolicyLevelCoverages,
  ProjectedUnitLevelCoverages,
} from "@redux/slices/endorsement";
import {
  FIELD_GROUP_PERILS,
  isFieldGroupPeril,
  type ExtraCoverageItem,
  type FieldGroup,
} from "@redux/slices/endorsement/fieldGroupPerils";

export type {
  ExtraCoverageItem,
  ExtraCoverageItemField,
  FieldGroup,
} from "@redux/slices/endorsement/fieldGroupPerils";

type UnitCoverageKey = keyof EndorsementExtraCoveragesFragment;
type PolicyCoverageKey = keyof ProjectedPolicyLevelCoverages;

type CoverageKey = UnitCoverageKey | PolicyCoverageKey;

/** One coverage entry, whatever its concrete value type is. */
type RawCoverage =
  | ProjectedUnitLevelCoverages[UnitCoverageKey]
  | ProjectedPolicyLevelCoverages[PolicyCoverageKey];

/**
 * Form value of a single extra coverage. `false`/`null` means the coverage is
 * off; an array (even an empty one) means an item coverage is on.
 */
export type ExtraCoverageValue =
  | boolean
  | string
  | number
  | ExtraCoverageItem[]
  | null;

export interface EndorsementExtraCoverage {
  key: CoverageKey;
  name: string;
  description: string;
  tooltip: string;
  image: string;
  /** When false the coverage is shown but its toggle is not interactive. */
  isCustomizable: boolean;
  /** Value from the projected policy. */
  value: ExtraCoverageValue;
  /** Value written when the agent switches the coverage on. */
  valueWhenEnabled: ExtraCoverageValue;
  /** Inline dropdown options; empty when the value is not selectable. */
  options: OptionType<string | number>[];
  /** Present only for coverages holding structured sub-items. */
  fieldGroup?: FieldGroup;
  /** Renders the current value as a read-only label, e.g. `Coverage Amount: $100,000`. */
  getValueFormats: (
    value: ExtraCoverageValue
  ) => CoverageValueFormats | undefined;
}

const coverageDetails = getExtraCoveragesMap(
  ScreenWithCoverages.ExtraCoverages
);

const PERIL_VALUE_TO_FORM_VALUE: Partial<
  Record<CoverageKey, (value: never) => string | number>
> = {
  golfCarts: mapNumberEnumToNumber,
  earthquake: mapPercentEnumToDisplayValue,
  debrisRemoval: mapDollarsEnumToNumber,
  lossAssessment: mapDollarsEnumToNumber,
  residenceBurglary: mapDollarsEnumToNumber,
  waterBackupAndSumpOverflow: mapDollarsEnumToNumber,
};

/** Non-enum values — `null` when off, numbers already in form shape — pass through. */
const toFormValue = (key: CoverageKey, value: RawCoverage["value"]) =>
  typeof value === "string"
    ? PERIL_VALUE_TO_FORM_VALUE[key]?.(value as never) ?? value
    : value;

/** Only enum and boolean coverages carry these; item coverages do not. */
const defaultValueOf = (raw: RawCoverage) =>
  "defaultValue" in raw ? raw.defaultValue : undefined;

const optionsOf = (raw: RawCoverage) =>
  ("valueCustomization" in raw ? raw.valueCustomization?.options : undefined) ??
  undefined;

/** A coverage is on unless it is explicitly `false` or absent. */
export const isExtraCoverageOn = (value: ExtraCoverageValue) =>
  typeof value === "boolean" ? value : value != null;

/** Value written back when a coverage is switched off. */
export const isDisabledValue = (coverage: EndorsementExtraCoverage) =>
  typeof coverage.valueWhenEnabled === "boolean" ? false : null;

const buildValueWhenEnabled = (
  key: CoverageKey,
  raw: RawCoverage,
  fieldGroup: FieldGroup | undefined,
  options: OptionType<string | number>[]
): ExtraCoverageValue => {
  if (fieldGroup) return [];
  if (typeof raw.value === "boolean") return true;

  return (toFormValue(key, defaultValueOf(raw)) ??
    options[0]?.value ??
    null) as string | number | null;
};

export const buildExtraCoverages = (
  unitLevelCoverages: ProjectedUnitLevelCoverages,
  policyLevelCoverages: ProjectedPolicyLevelCoverages | undefined,
  nameOptions: CoverageOptions
): EndorsementExtraCoverage[] =>
  extraCoveragesPageAccessors
    .flatMap((accessor) => {
      const key = accessor.split(".")[0] as CoverageKey;
      const details = coverageDetails[accessor as ExtraCoverageAccessor];
      const raw: RawCoverage | undefined =
        details?.level === CoverageLevel.Policy
          ? policyLevelCoverages?.[key as PolicyCoverageKey]
          : unitLevelCoverages[key as UnitCoverageKey];

      if (!raw || !details || raw.valueIsHidden) return [];

      const fieldGroup = isFieldGroupPeril(key)
        ? FIELD_GROUP_PERILS[key]
        : undefined;
      const options = formatCustomizationOptions(optionsOf(raw));
      const valueWhenEnabled = buildValueWhenEnabled(
        key,
        raw,
        fieldGroup,
        options
      );

      const formValue = toFormValue(key, raw.value);
      const coverageValue: ExtraCoverageValue = Array.isArray(formValue)
        ? (formValue as ExtraCoverageItem[])
        : formValue ?? null;

      return {
        key,
        name: details.getName(nameOptions),
        description: details.getDescription(nameOptions) ?? "",
        tooltip: details.getTooltip(nameOptions) ?? "",
        image: details.image,
        isCustomizable: raw.valueIsCustomizable,
        value: coverageValue,
        valueWhenEnabled,
        options,
        fieldGroup,
        getValueFormats: (value) =>
          details.getValueFormats(value as never) ?? undefined,
      };
    })
    .sort(compareExtraCoveragesOnPage);
